import { useState, useRef, useEffect, useMemo } from 'react'
import type { Product } from '../types'
import {
  ProductCategories,
  ProductCategoryLabels,
  ProductStatuses,
} from '@esf/shared'

// Simple icon renderer
const renderIcon = (path: string, className = 'w-5 h-5') => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
)

// Category badge component
const CategoryBadge = ({ category }: { category: string }) => {
  const colors: Record<string, string> = {
    seating: 'bg-blue-100 text-blue-700',
    litter_recycling: 'bg-green-100 text-green-700',
    shelters: 'bg-purple-100 text-purple-700',
    lighting: 'bg-yellow-100 text-yellow-700',
    signage: 'bg-orange-100 text-orange-700',
    planters: 'bg-emerald-100 text-emerald-700',
    bollards_barriers: 'bg-red-100 text-red-700',
    cycle_parking: 'bg-cyan-100 text-cyan-700',
    ev_charging: 'bg-indigo-100 text-indigo-700',
    digital_displays: 'bg-pink-100 text-pink-700',
  }
  return (
    <span className={`badge ${colors[category] || 'bg-gray-100 text-gray-700'}`}>
      {ProductCategoryLabels[category as keyof typeof ProductCategoryLabels] || category}
    </span>
  )
}

// Status badge component
const StatusBadge = ({ status }: { status: string }) => {
  const colors: Record<string, string> = {
    ACTIVE: 'bg-success-100 text-success-700',
    INACTIVE: 'bg-gray-100 text-gray-500',
    MAINTENANCE: 'bg-warning-100 text-warning-700',
    REPAIR_NEEDED: 'bg-error-100 text-error-700',
    DECOMMISSIONED: 'bg-gray-100 text-gray-500',
  }
  return (
    <span className={`badge ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  )
}

interface ProductsPageProps {
  products: Product[]
  loading: boolean
  error: string | null
  searchQuery: string
  setSearchQuery: (v: string) => void
  categoryFilter: string
  setCategoryFilter: (v: string) => void
  statusFilter: string
  setStatusFilter: (v: string) => void
  showDeleted: boolean
  setShowDeleted: (v: boolean) => void
  navigateTo: (page: string, id?: string) => void
  fetchProducts: () => void
  setSelectedProduct: (product: Product) => void
  setProductDetailModalOpen: (v: boolean) => void
  onRestoreProduct: (product: Product) => void
}

export default function ProductsPage({
  products,
  loading,
  error,
  searchQuery,
  setSearchQuery,
  categoryFilter,
  setCategoryFilter,
  statusFilter,
  setStatusFilter,
  showDeleted,
  setShowDeleted,
  navigateTo,
  fetchProducts,
  setSelectedProduct,
  setProductDetailModalOpen,
  onRestoreProduct,
}: ProductsPageProps) {
  // Local search state for immediate feedback
  const [searchInput, setSearchInput] = useState(searchQuery)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 25

  // Sync searchInput with searchQuery when it changes externally
  useEffect(() => {
    setSearchInput(searchQuery)
  }, [searchQuery])

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value)
    }, 300)
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  // Filter products
  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        const matchesSearch =
          p.name?.toLowerCase().includes(q) ||
          p.passportId?.toLowerCase().includes(q) ||
          p.id?.toLowerCase().includes(q) ||
          p.serialNumber?.toLowerCase().includes(q) ||
          p.model?.toLowerCase().includes(q)
        if (!matchesSearch) return false
      }
      if (categoryFilter && p.category !== categoryFilter) return false
      if (statusFilter && p.status !== statusFilter) return false
      return true
    })
  }, [products, searchQuery, categoryFilter, statusFilter])

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, categoryFilter, statusFilter])

  // Paginated products
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage)
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage
    return filteredProducts.slice(start, start + itemsPerPage)
  }, [filteredProducts, currentPage, itemsPerPage])

  const clearFilters = () => {
    setSearchInput('')
    setSearchQuery('')
    setCategoryFilter('')
    setStatusFilter('')
    setShowDeleted(false)
    setCurrentPage(1)
  }

  return (
    <div className="container-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Product Registry</h1>
        <button
          className="btn btn-primary"
          onClick={() => navigateTo('product-create')}
        >
          {renderIcon('M12 4v16m8-8H4')}
          Add Product
        </button>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <input
          type="text"
          placeholder="Search products..."
          className="filter-input flex-1"
          value={searchInput}
          onChange={e => handleSearchChange(e.target.value)}
        />
        <select
          className="filter-select"
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
        >
          <option value="">All Categories</option>
          {ProductCategories.map(c => <option key={c} value={c}>{ProductCategoryLabels[c]}</option>)}
        </select>
        <select
          className="filter-select"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          {ProductStatuses.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
            className="rounded border-gray-300"
          />
          Show deleted
        </label>
        <button onClick={fetchProducts} className="btn btn-secondary">
          {renderIcon('M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15')}
        </button>
        {(searchInput || categoryFilter || statusFilter || showDeleted) && (
          <button onClick={clearFilters} className="btn btn-outline text-sm">
            Clear filters
          </button>
        )}
      </div>

      {/* Product Table */}
      <div className="table-card">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-esf-navy"></div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Passport ID</th>
                <th>Category</th>
                <th>Subcategory</th>
                <th>Status</th>
                <th>Condition</th>
                <th>Location</th>
                <th className="text-center">Chain</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedProducts.map(product => (
                <tr key={product.id} className={product.deletedAt ? 'opacity-50 bg-gray-50' : ''}>
                  <td className="font-medium">{product.name}</td>
                  <td className="font-mono text-sm text-gray-500">{product.passportId}</td>
                  <td><CategoryBadge category={product.category} /></td>
                  <td className="text-sm text-gray-600">{product.subcategory || '-'}</td>
                  <td>
                    {product.deletedAt ? (
                      <span className="badge bg-red-100 text-red-700">Deleted</span>
                    ) : (
                      <StatusBadge status={product.status} />
                    )}
                  </td>
                  <td>
                    <span className={`badge badge-condition-${(product.condition || 'good').toLowerCase()}`}>
                      {product.condition || 'Good'}
                    </span>
                  </td>
                  <td className="text-sm text-gray-500">
                    {product.location ? `${product.location.lat?.toFixed(4)}, ${product.location.lng?.toFixed(4)}` : '-'}
                  </td>
                  <td className="text-center">
                    {product.blockchainTxId ? (
                      <span
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 cursor-help"
                        title={`Tx: ${product.blockchainTxId}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      </span>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => {
                          setSelectedProduct(product)
                          setProductDetailModalOpen(true)
                        }}
                      >
                        View
                      </button>
                      {product.deletedAt ? (
                        <button
                          className="btn btn-sm bg-success-50 text-success-600 hover:bg-success-100"
                          onClick={() => onRestoreProduct(product)}
                          title="Restore product"
                        >
                          Restore
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => navigateTo('product-edit', product.id)}
                        >
                          Edit
                        </button>
                      )}
                      {(product as any).materialComposition ? (
                        <a
                          href={`#/public/passport/${product.passportId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                          title="View public passport"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                          DPP
                        </a>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
              {paginatedProducts.length === 0 && (
                <tr>
                  <td colSpan={9}>
                    <div className="empty-state">
                      <div className="empty-state-icon">
                        {renderIcon('M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10')}
                      </div>
                      <div className="empty-state-title">No products found</div>
                      <div className="empty-state-text">
                        {searchQuery || statusFilter || categoryFilter
                          ? 'Try adjusting your filters'
                          : 'Get started by adding your first product'}
                      </div>
                      {!searchQuery && !statusFilter && !categoryFilter && (
                        <button
                          className="btn btn-primary mt-4"
                          onClick={() => navigateTo('product-create')}
                        >
                          {renderIcon('M12 4v16m8-8H4')}
                          Add Product
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {filteredProducts.length > 0 && (
        <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white border border-gray-200 rounded-lg">
          <div className="text-sm text-gray-600">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredProducts.length)} of {filteredProducts.length} products
          </div>
          <div className="flex items-center gap-2">
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              First
            </button>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 bg-error-100 text-error-600 rounded-lg">{error}</div>
      )}
    </div>
  )
}
